import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { ContainerPosition } from "../models/marine.traffic";
import MarineTrafficService from "../services/marine.traffic.service";

const useActionMarineTraffic = () => {
  const { t } = useTranslation();

  const useContainerSubscription = (callback: (marineTrafficId: string) => void) => {
    return useMutation(
      ["container.subscription"],
      (containerId: string) =>
        MarineTrafficService.subscribe({
          scac: "AUTO",
          tdId: containerId,
          tdType: "CN",
        }),
      {
        onSuccess: (response) => {
          if (
            response &&
            response.data &&
            response.data.subscription &&
            response.data.subscription.shipmentId
          ) {
            callback(response.data.subscription.shipmentId);
          } else {
            callback("");
          }
        },
        onError: () => {
          NotificationToast.warning(t("couldnTSubscribeShipment"));
          callback("");
        },
      }
    );
  };

  const getContainerStatus = (
    setContainerStatus: (containerStatus: any) => void,
    shipmentId: string
  ) => {
    return useQuery(
      ["container.status", shipmentId],
      () => MarineTrafficService.status(shipmentId),
      {
        enabled: !!shipmentId,
        refetchOnWindowFocus: false,
        cacheTime: 0,
        retry: false,
        onSuccess: (response) => {
          if (response && response.data) {
            setContainerStatus(response.data);
          }
        },
        onError: () => NotificationToast.warning(t("couldnTRetrieveShipmentStatus")),
      }
    );
  };

  const getContainerPosition = (
    setContainerPosition: React.Dispatch<React.SetStateAction<ContainerPosition | undefined>>,
    vesselShipId: string,
    enabled: boolean = true
  ) => {
    return useQuery(
      ["container.status", vesselShipId],
      () => MarineTrafficService.containerPosition(vesselShipId),
      {
        enabled: !!vesselShipId && enabled,
        refetchOnWindowFocus: false,
        cacheTime: 0,
        retry: false,
        onSuccess: (response) => {
          if (response && response.data && response.data.length > 0) {
            const data = response.data[0];

            setContainerPosition(data);
          } else {
            setContainerPosition(undefined);
          }
        },
        onError: () => NotificationToast.error(t("couldnTRetrieveShipmentStatus")),
      }
    );
  };

  return {
    useContainerSubscription,
    getContainerStatus,
    getContainerPosition,
  };
};

export default useActionMarineTraffic;
